import { useState, useEffect } from 'react';
import { RateCard, CreateRateCardRequest, UpdateRateCardRequest } from '../../../shared/types/rateCard';

export const useRateCards = () => {
  const [rateCards, setRateCards] = useState<RateCard[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const fetchRateCards = async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await fetch(`/api/ratecards`);
      if (!response.ok) {
        throw new Error(`Failed to fetch rate cards: ${response.statusText}`);
      }
      const data = await response.json();
      const list = Array.isArray(data) ? data : (data?.rateCards || []);
      setRateCards(list);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to fetch rate cards');
      console.error('Error fetching rate cards:', err);
    } finally {
      setLoading(false);
    }
  };

  const createRateCard = async (rateCardData: CreateRateCardRequest): Promise<RateCard | null> => {
    try {
      const response = await fetch(`/api/ratecards`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(rateCardData),
      });

      if (!response.ok) {
        throw new Error(`Failed to create rate card: ${response.statusText}`);
      }

      const newRateCard = await response.json();
      setRateCards(prev => [...prev, newRateCard]);
      return newRateCard;
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to create rate card');
      console.error('Error creating rate card:', err);
      return null;
    }
  };

  const updateRateCard = async (rateCardData: UpdateRateCardRequest): Promise<RateCard | null> => {
    try {
      const response = await fetch(`/api/ratecards/${rateCardData.id}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(rateCardData),
      });

      if (!response.ok) {
        throw new Error(`Failed to update rate card: ${response.statusText}`);
      }

      const updatedRateCard = await response.json();
      setRateCards(prev => 
        prev.map(rateCard => 
          rateCard.id === rateCardData.id ? updatedRateCard : rateCard
        )
      );
      return updatedRateCard;
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to update rate card');
      console.error('Error updating rate card:', err);
      return null;
    }
  };

  const deleteRateCard = async (rateCard: RateCard): Promise<boolean> => {
    try {
      const response = await fetch(`/api/ratecards/${rateCard.id}`, {
        method: 'DELETE',
      });

      if (!response.ok) {
        throw new Error(`Failed to delete rate card: ${response.statusText}`);
      }

      setRateCards(prev => prev.filter(rc => rc.id !== rateCard.id));
      return true;
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to delete rate card');
      console.error('Error deleting rate card:', err);
      return false;
    }
  };

  useEffect(() => {
    fetchRateCards();
  }, []);

  return {
    rateCards,
    loading,
    error,
    fetchRateCards,
    createRateCard,
    updateRateCard,
    deleteRateCard,
  };
};
